import Character from "../model/character/Character";
import Crew from "../model/crew/Crew";
import GridRegion from "../model/grid/GridRegion";
import GridTile from "../model/grid/GridTile";
import GridZone from "../model/grid/GridZone";
import Rand from "./Rand";

export default class NearbyTileUtil {
    constructor() {
    }

    /**
     * Finds an available tile near the main miner that is not occupied
     * by any crew member.
     * @param {Character} character
     * @param {Crew} crew
     * @param {GridTile} tile 
     * @returns {GridTile}
     */
    static findAvailableTileNearMainMiner(character, crew) {
        return this.findAvailableNearbyTile(character, crew, crew.main.position.tile);
    }

    /**
     * Finds an available tile near the specified tile that is not occupied
     * by any crew member.
     * @param {Character} character
     * @param {Crew} crew
     * @param {GridTile} centerTile
     * @returns {GridTile}
     */
    static findAvailableNearbyTile(character, crew, centerTile) {

        // If the main miner does not have a tile, then the party if off the visible grid.
        if (!centerTile) {
            return null;
        }

        // 1. Just look at the tile neighbors.
        const directNeighbor = this.findAvailableNeighborTile(character, crew, centerTile);
        if (directNeighbor) {
            return directNeighbor;
        }

        // 2. Pick from the region tiles.
        const regionNeighbor = this.findAvailableRegionTile(character, crew, centerTile.region);
        if (regionNeighbor) {
            return regionNeighbor;
        }

        // Pick a random region tile
        const regionTile = this.forceFindTileInRegion(centerTile.region);
        if (regionTile) {
            return regionTile;
        }

        // 3. Pick from the zone tiles.
        const zoneNeighbor = this.findAvailableZoneTile(character, crew, centerTile.region.zone);
        if (zoneNeighbor) {
            return zoneNeighbor;
        }

        // 4. Force it.  Find a closed tile and make it open.
        const forcedNeighbor = this.forceFindTileInZone(centerTile.region.zone);
        if (forcedNeighbor) {
            return forcedNeighbor;
        }

        console.log('NearbyTileUtil.findAvailableTileNearMainMiner() Complete failure.');
        return null;
    }

    /**
     * @private
     * @param {Character} character
     * @param {Crew} crew
     * @param {GridTile} tile 
     * @returns {GridTile}
     */
    static findAvailableNeighborTile(character, crew, tile) {
        const neighbors = tile.neighbors;
        for (let i = 0; i < 5; i++) {
            const neighbor = neighbors[Rand.randomInt(neighbors.length)];
            if (neighbor && this.isTileClear(character, crew, neighbor)) {
                return neighbor;
            }
        }
        return null;
    }

    /**
     * @private
     * @param {Character} character
     * @param {Crew} crew 
     * @param {GridZone} zone 
     * @returns {GridTile}
     */
    static findAvailableZoneTile(character, crew, zone) {
        const regions = zone.regions;

        for (let i = 0; i < 5; i++) {
            const region = regions[Rand.randomInt(regions.length)];

            const zoneTile = this.findAvailableRegionTile(character, crew, region);
            if (zoneTile) {
                return zoneTile;
            }
        }

        return null;
    }

    /**
     * @private
     * @param {GridZone} zone 
     * @returns {GridTile}
     */
    static forceFindTileInZone(zone) {
        
        const regions = zone.regions;
        for (let i = 0; i < regions.length; i++) {
            const tile = this.forceFindTileInRegion(regions[i]);
            if (tile) {
                return tile;
            }
        }

        return null;
    }

    /**
     * @private
     * @param {Character} character
     * @param {Crew} crew 
     * @param {GridRegion} region 
     * @returns {GridTile}
     */
    static findAvailableRegionTile(character, crew, region) {
        const tiles = region.tiles;
        for (let i = 0; i < 12; i++) {
            const regionTile = tiles[Rand.randomInt(tiles.length)];
            if (regionTile && this.isTileClear(character, crew, regionTile)) {
                return regionTile;
            }
        }
        return null;
    }

    /**
     * @private
     * @param {GridRegion} region 
     * @returns {GridTile}
     */
    static forceFindTileInRegion(region) {
        if (!region) {
            return null;
        }

        const tile = region.tiles[Rand.randomInt(region.tiles.length)];
        if (!tile.open) {
            // We are going to force this tile to be available by breaking it open.
            tile.onTileBreak();
        }

        return tile;
    }

    /**
     * @private
     * @param {Character} character
     * @param {Crew} crew
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    static isClosedTileAvailable(character, crew, tile) {
        for (let i = 0; i < crew.miners.length; i++) {
            const miner = crew.miners[i];
            if (miner === character) {
                continue;
            }

            if (tile === miner.target.targetTile) {
                return false;
            }
        }

        return true;
    }

    /**
     * @private
     * @param {Character} character
     * @param {Crew} crew
     * @param {GridTile} tile 
     * @returns {boolean}
     */
    static isTileClear(character, crew, tile) {
        if (!tile || !tile.isTraversable()) {
            return false;
        }

        for (let i = 0; i < crew.miners.length; i++) {
            const miner = crew.miners[i];
            if (miner === character) {
                continue;
            }

            if (tile === miner.position.tile || tile === miner.target.targetTile) {
                return false;
            }
        }

        return true;
    }

}